import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Star, Loader2, Check, ShoppingCart, X, AlertTriangle,
  ArrowRight as IconArrowRight, Home, Save, Layers, SlidersHorizontal,
  Quote, RotateCcw, Search, GraduationCap, MapPin, Zap, LayoutGrid, LayoutList,
  AlertCircle
} from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import CollegeFilter from "./CollegeFilter";
import { getColleges, saveDraftSelection } from "../../services/api";
import { useTheme } from '../Home/ThemeContext';
import vidyazonLogo from '../../img/logo3.png';

const MIN_SELECTION = 5;
const MAX_SELECTION = 10;
const VIDYAZON_PRIMARY = 'indigo';

const formatFee = (fee) => {
  if (!fee) return 'N/A';
  if (typeof fee === 'string' && fee.match(/[^0-9]/) && fee.length < 15) return fee;
  const numericStr = String(fee).replace(/[^0-9.]/g, '');
  const numericFee = parseFloat(numericStr);
  if (isNaN(numericFee) || numericFee === 0) return 'N/A';
  if (numericFee >= 10000000) return `₹${(numericFee / 10000000).toFixed(2)} Cr`;
  if (numericFee >= 100000) return `₹${(numericFee / 100000).toFixed(2)} L`;
  return `₹${numericFee.toLocaleString('en-IN')}`;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
  const host = backendUrl.replace('/api', '');
  let cleanedPath = imagePath;
  if (cleanedPath.startsWith('/')) cleanedPath = cleanedPath.substring(1);
  if (cleanedPath.startsWith('uploads/')) cleanedPath = cleanedPath.substring(8);
  return `${host}/uploads/${cleanedPath}`;
};

// Auth helper
const getCurrentUser = () => {
  try {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    return { token, userType, user };
  } catch { return { token: null, userType: null, user: null }; }
};

export default function CollegeSelectionUI() {
  const context = useTheme();
  const isDarkMode = context?.isDarkMode || false;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef(null);
  // Guard: skip debounce on first render so URL-initialized search isn't wiped
  const initializedRef = useRef(false);

  const [colleges, setColleges] = useState([]);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionError, setSelectionError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // ✅ FIX: lazy init from URL ?q= param so search is active immediately on navigation from Hero
  const [searchQuery, setSearchQuery] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [filters, setFilters] = useState({
    disciplines: [], countries: [], continents: [], states: [],
    tuitionFee: { min: "", max: "" },
    durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [],
  });
  const [apiFilters, setApiFilters] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showMinSelectionPopup, setShowMinSelectionPopup] = useState(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [proceedingToForm, setProceedingToForm] = useState(false);
  const [authError, setAuthError] = useState("");

  const selectionCount = selectedColleges.length;
  const isDoubleFee = selectionCount > 5;

  const getActiveTab = useCallback(() => {
    if (location.pathname.includes('/student/programs')) return 'programs';
    if (location.pathname.includes('/student/scholarships')) return 'scholarships';
    return 'universities';
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTab());

  const theme = {
    bg: isDarkMode ? 'bg-slate-900' : 'bg-indigo-50',
    sidebar: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-indigo-100/70 border-indigo-200',
    header: isDarkMode ? 'bg-slate-800/95 border-slate-700' : 'bg-white/95 border-slate-200',
    card: isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
    cardHover: isDarkMode ? 'hover:border-indigo-600/50' : 'hover:border-indigo-400',
    textMain: isDarkMode ? 'text-white' : 'text-slate-900',
    textMuted: isDarkMode ? 'text-slate-400' : 'text-slate-500',
    input: isDarkMode ? 'bg-slate-700 border-slate-600 text-white focus:ring-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:ring-indigo-500',
    accent: 'from-indigo-600 to-indigo-700',
    accentText: 'text-indigo-600 dark:text-indigo-400',
    accentShadow: 'shadow-indigo-500/30',
    popupBg: isDarkMode ? 'bg-slate-800' : 'bg-white',
  };

  // ✅ FIX: Debounce user typing only — skip on mount (URL already initialized above)
  useEffect(() => {
    if (!initializedRef.current) { initializedRef.current = true; return; }
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Keep ?q= URL param in sync as user types
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (searchQuery) next.set('q', searchQuery); else next.delete('q');
        return next;
      }, { replace: true });
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]); // eslint-disable-line

  // ✅ FIX: Sync when URL changes externally (e.g. navigating from Hero with ?q=)
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setActiveTab(getActiveTab());
    // Only update if URL param differs (avoids overwriting user-typed search)
    if (q && q !== searchQuery) {
      setSearchQuery(q);
      setDebouncedSearch(q);
    }
  }, [searchParams.toString()]); // eslint-disable-line

  useEffect(() => {
    setPage(1);
    loadColleges(1);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [debouncedSearch, apiFilters]);

  useEffect(() => {
    if (page > 1) loadColleges(page);
  }, [page]);

  const loadColleges = async (currentPage = page) => {
    try {
      setLoading(true);
      const res = await getColleges({ page: currentPage, limit: 10, q: debouncedSearch, ...apiFilters });
      const backend = res.data;
      const newColleges = backend.data || [];
      setColleges(prev => currentPage === 1 ? newColleges : [...prev, ...newColleges]);
      setTotalPages(backend.totalPages || 1);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // Build API params and apply immediately
    const params = {};
    if (newFilters.disciplines?.length) params.courses = newFilters.disciplines.join(',');
    if (newFilters.countries?.length) params.countries = newFilters.countries.join(',');
    if (newFilters.states?.length) params.states = newFilters.states.join(',');
    if (newFilters.continents?.length) params.continents = newFilters.continents.join(',');
    if (newFilters.tuitionFee?.min || newFilters.tuitionFee?.max) {
      params.tuitionFee = `${newFilters.tuitionFee.min || 0},${newFilters.tuitionFee.max || 100000000}`;
    }
    if (newFilters.durations?.length) params.durations = newFilters.durations.join(',');
    if (newFilters.format?.length) params.format = newFilters.format.join(',');
    if (newFilters.attendance?.length) params.attendance = newFilters.attendance.join(',');
    if (newFilters.degreeTypes?.length) params.degreeTypes = newFilters.degreeTypes.join(',');
    if (newFilters.specialProgrammes?.length) params.specialProgrammes = newFilters.specialProgrammes.join(',');
    setApiFilters(params);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileFilterOpen(false);
  }, []);

  const clearAllFilters = () => {
    setFilters({ disciplines: [], countries: [], continents: [], states: [], tuitionFee: { min: "", max: "" }, durations: [], format: [], attendance: [], degreeTypes: [], specialProgrammes: [] });
    setApiFilters({});
    setSearchQuery("");
    setDebouncedSearch("");
    setPage(1);
    // ✅ FIX: Also clear ?q= from URL
    setSearchParams({}, { replace: true });
  };

  const getCollegeId = (c) => c._id || c.id;
  const isSelected = (college) => selectedColleges.some(c => getCollegeId(c) === getCollegeId(college));

  const toggleCollegeSelection = (college) => {
    const id = getCollegeId(college);
    if (!id) return;
    setSelectedColleges(prev => {
      const exists = prev.some(c => getCollegeId(c) === id);
      if (exists) { setSelectionError(""); return prev.filter(c => getCollegeId(c) !== id); }
      if (prev.length >= MAX_SELECTION) { setSelectionError(`Limit Reached: Maximum ${MAX_SELECTION} colleges allowed.`); return prev; }
      setSelectionError("");
      return [...prev, college];
    });
  };

  const handleSaveDraft = async () => {
    const { token, userType } = getCurrentUser();
    setAuthError("");
    if (!token) {
      localStorage.setItem('tempDraftSelection', JSON.stringify(selectedColleges));
      navigate('/login?type=student', { state: { returnUrl: location.pathname } });
      return;
    }
    if (userType === 'partner' || userType === 'college') {
      setAuthError(`You are logged in as ${userType}. Please login as a Student to save your selection.`);
      return;
    }
    if (userType !== 'student') {
      setAuthError("Please login as a Student to save your selection.");
      return;
    }
    if (selectedColleges.length === 0) { setSelectionError("Please select at least one college."); return; }

    try {
      setSavingDraft(true);
      setSelectionError("");
      const validIds = selectedColleges.map(c => { const id = c._id || c.id || c.collegeId; return id ? String(id) : null; }).filter(Boolean);
      if (validIds.length === 0) { setSelectionError("Error: Could not find valid IDs."); return; }
      await saveDraftSelection({ collegeIds: validIds });
      setShowSaveSuccess(true);
    } catch (err) {
      const backendMsg = err.response?.data?.message || err.response?.data?.error;
      setSelectionError(backendMsg || "Failed to save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleProceedToForm = async () => {
    const { token, userType } = getCurrentUser();
    setAuthError("");
    if (!token) { navigate('/login?type=student', { state: { returnUrl: location.pathname } }); return; }
    if (userType === 'partner' || userType === 'college') {
      setAuthError(`You are logged in as ${userType}. Please login as a Student to proceed.`);
      return;
    }
    try {
      setProceedingToForm(true);
      const selectionData = {
        colleges: selectedColleges.map(c => ({ id: getCollegeId(c), name: c.collegeName, logoUrl: c.logoUrl || c.collegeLogo || c.imageUrl })),
        totalSelected: selectedColleges.length,
        isDoubleFee
      };
      localStorage.setItem('pendingCollegeSelection', JSON.stringify(selectionData));
      navigate('/student-form', { state: { selectedColleges: selectionData.colleges, isDoubleFee } });
    } catch (err) {
      console.error(err);
    } finally {
      setProceedingToForm(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'programs') navigate('/student/programs' + location.search);
    else if (tab === 'scholarships') navigate('/student/scholarships' + location.search);
    else navigate('/student/colleges' + location.search);
  };

  // Grid Card Component
  const GridCard = ({ college }) => {
    const id = getCollegeId(college);
    const selected = isSelected(college);
    const logoUrl = getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl);
    const course = college.courses?.[0] || {};
    const fee = formatFee(course?.Fees || college.feeMin_INR);

    return (
      <div className={`group relative rounded-2xl border transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 overflow-hidden flex flex-col
        ${selected ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500/50' : `${theme.card} ${theme.cardHover}`}`}>
        {selected && (
          <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl z-10">SELECTED</div>
        )}
        <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
          <div className="w-full h-24 flex items-center justify-center bg-white rounded-xl mb-3 border border-slate-100 p-2">
            {logoUrl ? (
              <img src={logoUrl} alt={college.collegeName} className="max-w-full max-h-full object-contain"
                onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/80"; }} />
            ) : (
              <GraduationCap className="w-10 h-10 text-indigo-300" />
            )}
          </div>
          <h3 className={`font-bold text-sm text-center line-clamp-2 group-hover:text-indigo-600 transition-colors ${theme.textMain}`}>
            {college.collegeName}
          </h3>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-2">
          <div className={`flex items-center gap-1 text-xs ${theme.textMuted}`}>
            <MapPin className="w-3 h-3 text-indigo-400 flex-shrink-0" />
            <span className="truncate">{college.city}{college.city && college.state ? ', ' : ''}{college.state}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-xs font-bold text-amber-500">
              <Star className="w-3 h-3 fill-current" /> {college.rating || "N/A"}
            </span>
            {fee !== 'N/A' && <span className="text-xs font-bold text-emerald-600">{fee}</span>}
          </div>
          <div className="mt-auto pt-2 flex gap-2">
            <button onClick={() => toggleCollegeSelection(college)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1 transition-all active:scale-95 text-white
                ${selected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110'}`}>
              {selected ? <><Check className="w-3 h-3" /> Added</> : 'Select'}
            </button>
            <button onClick={() => navigate(`/college/${getCollegeId(college)}`)}
              className={`p-2 rounded-xl border text-xs font-bold transition-all ${theme.card} text-indigo-600 hover:bg-indigo-50`}>
              <IconArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // List Card Component
  const ListCard = ({ college }) => {
    const id = getCollegeId(college);
    const selected = isSelected(college);
    const logoUrl = getImageUrl(college.logoUrl || college.collegeLogo || college.imageUrl);
    const course = college.courses?.[0] || {};
    const fee = formatFee(course?.Fees || college.feeMin_INR);

    return (
      <div className={`group relative rounded-2xl border transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10 overflow-hidden
        ${selected ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500/50' : `${theme.card} ${theme.cardHover}`}`}>
        {selected && (
          <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl z-10">SELECTED</div>
        )}
        <div className="flex flex-col sm:flex-row p-4 sm:p-6 gap-4 sm:gap-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0 w-16 h-16 sm:w-24 sm:h-24 rounded-2xl border bg-white p-2 shadow-sm flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
              {logoUrl ? (
                <img src={logoUrl} alt={college.collegeName} className="max-w-full max-h-full object-contain"
                  onError={e => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/80"; }} />
              ) : (
                <GraduationCap className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-300" />
              )}
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <h3 className={`text-base sm:text-xl font-bold truncate pr-4 mb-1 group-hover:text-indigo-600 transition-colors ${theme.textMain}`}>
                {college.collegeName}
              </h3>
              {(college.country || college.continent) && (
                <p className={`text-xs sm:text-sm mb-1 font-medium ${theme.textMuted}`}>
                  <span className="font-semibold">{college.country || 'Global'}</span>
                  {college.continent && <span className="text-xs opacity-70"> ({college.continent})</span>}
                </p>
              )}
              <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm mb-2">
                <span className={`flex items-center gap-1 ${theme.textMuted}`}>
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-400" />
                  {college.city}{college.city && college.state ? ', ' : ''}{college.state}
                </span>
                <span className="flex items-center gap-1 font-bold text-amber-500">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-current" /> {college.rating || "N/A"}
                </span>
                {college.established && (
                  <span className={`flex items-center gap-1 ${theme.textMuted}`}>
                    <GraduationCap className="w-3 h-3" /> Est. {college.established}
                  </span>
                )}
              </div>
              {course?.name && (
                <div className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${isDarkMode ? 'bg-slate-700 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                  <Layers className="w-3 h-3" />
                  <span className="truncate max-w-[160px]">{course.name}</span>
                  {college.courses?.length > 1 && <span className="opacity-60">+{college.courses.length - 1}</span>}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row sm:flex-col items-center justify-between sm:justify-end gap-3 sm:gap-4 sm:min-w-[130px] sm:border-l sm:border-slate-100 sm:dark:border-slate-800 sm:pl-5">
            {fee !== 'N/A' && (
              <div className={`p-2 sm:p-3 rounded-xl border border-slate-200/50 ${theme.popupBg}`}>
                <span className="text-base sm:text-xl font-extrabold text-emerald-600 block">{fee}</span>
                <p className="text-xs font-medium mt-0.5 text-slate-500">Total Fee</p>
              </div>
            )}
            <div className="flex sm:flex-col gap-2 w-auto sm:w-full">
              <button onClick={() => toggleCollegeSelection(college)}
                className={`py-2 px-3 sm:w-full rounded-xl text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95 text-white shadow-lg shadow-indigo-500/30
                  ${selected ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110'}`}>
                {selected ? <><Check className="w-4 h-4" /> Added</> : 'Select'}
              </button>
              <button onClick={() => navigate(`/college/${getCollegeId(college)}`)}
                className={`py-2 px-3 sm:w-full text-center text-xs font-bold transition-all rounded-xl border ${theme.card} text-indigo-600 hover:bg-indigo-50`}>
                Details
              </button>
            </div>
          </div>
        </div>
        {selected && (
          <div className={`px-4 sm:px-6 py-2 border-t-2 border-indigo-200 text-xs font-bold flex items-center gap-2 ${isDarkMode ? 'bg-indigo-900/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
            <Check className="w-3 h-3" /> Added — {selectedColleges.length}/{MAX_SELECTION} selected
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`h-[calc(100vh-64px)] mt-16 w-full flex flex-col overflow-hidden font-sans ${theme.bg}`}>

      {/* AUTH ERROR MODAL */}
      {authError && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className={`${theme.popupBg} rounded-3xl shadow-2xl max-w-sm w-full p-8 border border-red-200`}>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className={`text-xl font-bold text-center mb-2 ${theme.textMain}`}>Access Restricted</h3>
            <p className={`text-sm text-center mb-6 ${theme.textMuted}`}>{authError}</p>
            <div className="space-y-3">
              <button onClick={() => { setAuthError(""); navigate('/student/dashboard'); }}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-indigo-700">
                Go to Student Dashboard
              </button>
              <button onClick={() => setAuthError("")}
                className={`w-full py-3 rounded-xl font-bold ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SELECTION RULES POPUP */}
      {showMinSelectionPopup && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className={`${theme.popupBg} rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-indigo-600/20`}>
            <div className="h-28 bg-gradient-to-r from-indigo-600 to-indigo-700 relative p-6 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-full p-1">
                  <img src={vidyazonLogo} alt="Vidyazon Logo" className="h-8 w-auto" />
                </div>
                <span className="text-white text-xl font-extrabold tracking-wider">VIDYAZON</span>
              </div>
              <div className="absolute top-4 right-6 text-white opacity-20">
                <Layers className="w-24 h-24" />
              </div>
            </div>
            <div className="pt-4 px-6 sm:px-8 pb-8">
              <div className={`p-4 rounded-xl mb-6 border border-indigo-300 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}`}>
                <p className={`italic text-sm font-medium text-center ${theme.textMuted}`}>
                  "The journey of a thousand miles begins with the first selection. Choose your future now."
                </p>
              </div>
              <h3 className={`text-2xl font-bold ${theme.textMain} mb-2`}>Selection Strategy</h3>
              <p className={`${theme.textMuted} mb-6 text-sm`}>
                To maximize your admission chances, select between{' '}
                <span className="text-indigo-600 font-bold">{MIN_SELECTION} – {MAX_SELECTION} colleges</span>.
              </p>
              <div className="space-y-3 mb-8">
                <div className={`flex items-center justify-between p-4 rounded-xl border-l-4 border-emerald-500 ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-100'}`}>
                  <div>
                    <p className="font-bold text-emerald-700 dark:text-emerald-400">Standard Fee</p>
                    <p className="text-xs text-emerald-600/80">Best Value</p>
                  </div>
                  <span className="font-mono font-bold text-lg text-emerald-600">1 – 5 Colleges</span>
                </div>
                <div className={`flex items-center justify-between p-4 rounded-xl border-l-4 border-amber-500 ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-100'}`}>
                  <div>
                    <p className="font-bold text-amber-700 dark:text-amber-400">Double Fee</p>
                    <p className="text-xs text-amber-600/80">Maximum Exposure</p>
                  </div>
                  <span className="font-mono font-bold text-lg text-amber-600">6 – 10 Colleges</span>
                </div>
              </div>
              <button onClick={() => setShowMinSelectionPopup(false)}
                className="w-full py-4 rounded-xl font-bold text-white shadow-xl shadow-indigo-500/30 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:brightness-110 active:scale-[0.98] transition-all mb-4">
                Start Selection
              </button>
              <div className="text-center">
                <a href="/services/counselling" className="text-sm font-bold underline decoration-2 underline-offset-4 text-indigo-600 hover:opacity-80"
                  onClick={() => setShowMinSelectionPopup(false)}>
                  Still confused? Get Expert Counselling
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SAVE SUCCESS MODAL */}
      {showSaveSuccess && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className={`${theme.popupBg} rounded-[2rem] shadow-2xl max-w-sm w-full border border-indigo-300 relative overflow-hidden`}>
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-indigo-600 to-indigo-700 opacity-10"></div>
            <div className="p-8 flex flex-col items-center text-center relative z-10">
              <div className="w-20 h-20 bg-gradient-to-tr from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-6">
                <Check className="w-10 h-10 text-white stroke-[3]" />
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${theme.textMain}`}>Draft Saved!</h3>
              <p className={`text-sm mb-8 ${theme.textMuted}`}>
                Your selection of <span className="font-bold text-indigo-500">{selectedColleges.length} colleges</span> has been securely saved.
              </p>
              <div className="w-full space-y-3">
                <button onClick={() => navigate('/student/dashboard')}
                  className="w-full py-3.5 rounded-xl font-bold text-white shadow-lg active:scale-95 transition-all bg-gradient-to-r from-indigo-600 to-indigo-700">
                  Go to Dashboard
                </button>
                <button onClick={() => setShowSaveSuccess(false)}
                  className={`w-full py-3.5 rounded-xl font-bold transition-colors ${isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                  Continue Searching
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden relative">

        {/* SIDEBAR Desktop */}
        <aside className={`hidden lg:block w-64 xl:w-72 h-full flex-none overflow-y-auto custom-scrollbar z-20 border-r ${theme.sidebar}`}>
          <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
        </aside>

        {/* MOBILE FILTER DRAWER */}
        {mobileFilterOpen && (
          <div className="fixed inset-0 z-[100] flex justify-end lg:hidden">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileFilterOpen(false)} />
            <div className={`relative w-[90%] max-w-xs h-full shadow-2xl flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
              <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                <h3 className={`text-lg font-bold ${theme.textMain}`}>Filter Colleges</h3>
                <button onClick={() => setMobileFilterOpen(false)} className={`p-2 rounded-full ${theme.textMuted}`}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <CollegeFilter currentFilters={filters} onFilterChange={handleFilterChange} onClearAll={clearAllFilters} onApplyFilters={handleApplyFilters} />
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col h-full min-w-0 relative">
          {/* STICKY HEADER */}
          <div className={`flex-none z-10 backdrop-blur-md border-b shadow-sm ${theme.header}`}>
            <div className="px-4 sm:px-8 pt-4 sm:pt-5 pb-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 w-full group">
                <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors pointer-events-none" />
                <input type="text" placeholder="Search universities, cities..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-12 pr-10 py-3 rounded-full border outline-none ring-offset-2 focus:ring-2 transition-all font-medium shadow-sm text-sm sm:text-base ${theme.input}`} />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setDebouncedSearch(''); setSearchParams({}, { replace: true }); }}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                )}
              </div>
              <div className={`flex items-center gap-2`}>
                <div className={`flex p-1.5 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} border border-slate-200/50`}>
                  {['universities', 'programs', 'scholarships'].map(tab => (
                    <button key={tab} onClick={() => handleTabChange(tab)}
                      className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold rounded-full transition-all capitalize
                        ${activeTab === tab ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-md scale-105' : 'text-slate-500 hover:text-slate-800'}`}>
                      {tab}
                    </button>
                  ))}
                </div>
                {/* ✅ FIX: View Toggle — clearly visible with labels */}
                <div className={`flex items-center rounded-xl border-2 overflow-hidden flex-shrink-0 ${isDarkMode ? 'border-slate-600 bg-slate-800' : 'border-indigo-200 bg-white shadow-sm'}`}>
                  <button onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all border-r ${isDarkMode ? 'border-slate-600' : 'border-indigo-100'}
                      ${viewMode === 'list' ? 'bg-indigo-600 text-white' : `${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-indigo-50'}`}`}>
                    <LayoutList className="w-4 h-4" />
                    <span className="hidden sm:inline">List</span>
                  </button>
                  <button onClick={() => setViewMode('grid')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all
                      ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : `${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-indigo-50'}`}`}>
                    <LayoutGrid className="w-4 h-4" />
                    <span className="hidden sm:inline">Grid</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Progress Row */}
            <div className="px-4 sm:px-8 pb-4 pt-2 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-base sm:text-lg font-bold border-2 ${selectedColleges.length >= MIN_SELECTION ? 'border-indigo-600 text-indigo-600 bg-indigo-50' : 'border-slate-300 text-slate-400'}`}>
                    {selectedColleges.length}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-slate-900 dark:bg-white rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white dark:text-slate-900">
                    {MAX_SELECTION}
                  </div>
                </div>
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-bold uppercase tracking-wider ${theme.textMuted}`}>Selection</span>
                    {isDoubleFee
                      ? <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">Double Fee</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">Standard Fee</span>
                    }
                  </div>
                  <div className="w-24 sm:w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-500 rounded-full ${selectedColleges.length > 5 ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-emerald-400 to-teal-500'}`}
                      style={{ width: `${Math.min((selectedColleges.length / MAX_SELECTION) * 100, 100)}%` }} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3 flex-shrink-0">
                <button onClick={() => navigate('/student/dashboard')} className={`p-2 sm:p-2.5 rounded-xl border transition-colors ${theme.card} hover:bg-slate-50`}>
                  <Home className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                </button>
                <button onClick={handleSaveDraft} disabled={savingDraft || selectedColleges.length === 0}
                  className={`hidden sm:flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold border transition-all hover:bg-slate-50 ${theme.card} ${theme.textMain} disabled:opacity-50`}>
                  {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-indigo-600" />}
                  {savingDraft ? 'Saving...' : 'Draft'}
                </button>
                <button onClick={() => setShowCheckout(true)} disabled={selectedColleges.length < MIN_SELECTION}
                  className={`flex items-center gap-1.5 px-4 py-2 sm:px-6 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white shadow-lg shadow-indigo-500/30 transition-all active:scale-95
                    ${selectedColleges.length >= MIN_SELECTION ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:-translate-y-0.5' : 'bg-slate-300 cursor-not-allowed opacity-70'}`}>
                  <ShoppingCart className="w-4 h-4" /> <span className="hidden xs:inline">Checkout</span>
                </button>
              </div>
            </div>
            {(selectionError || authError) && (
              <div className="mx-4 sm:mx-8 mb-2 py-2 px-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-xs font-bold text-red-600">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {selectionError || authError}
              </div>
            )}
          </div>

          {/* COLLEGE LIST */}
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 sm:p-6 lg:p-8 xl:p-10 custom-scrollbar scroll-smooth bg-dots-pattern">
            {loading && page === 1 ? (
              <div className="flex flex-col items-center justify-center py-32 opacity-70">
                <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                <p className={`font-medium animate-pulse ${theme.textMuted}`}>Curating top universities for you...</p>
              </div>
            ) : colleges.length > 0 ? (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 w-full'
                : 'grid grid-cols-1 gap-4 sm:gap-5 w-full'
              }>
                {colleges.map(college => viewMode === 'grid'
                  ? <GridCard key={getCollegeId(college)} college={college} />
                  : <ListCard key={getCollegeId(college)} college={college} />
                )}
                {page < totalPages && (
                  <div className={`${viewMode === 'grid' ? 'col-span-full' : ''} py-8 flex justify-center`}>
                    <button onClick={() => setPage(p => p + 1)}
                      className={`px-8 sm:px-10 py-3 rounded-full font-bold shadow-lg hover:-translate-y-1 transition-all flex items-center gap-2 group ${theme.card} text-indigo-600`}>
                      {loading ? <Loader2 className="animate-spin" /> : <>Load More <IconArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8">
                <div className="mb-8 relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-20 blur-xl rounded-full" />
                  <div className={`relative ${theme.popupBg} p-6 rounded-3xl shadow-xl border border-slate-100`}>
                    <img src={vidyazonLogo} alt="Vidyazon" className="h-24 w-auto object-contain" />
                  </div>
                </div>
                <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${theme.textMain}`}>No Matches Found</h2>
                <p className={`${theme.textMuted} max-w-md text-center mb-8 text-sm sm:text-base`}>Try adjusting your search or filters.</p>
                <button onClick={clearAllFilters}
                  className="px-8 py-3 rounded-full font-bold shadow-lg hover:-translate-y-1 transition-all flex items-center gap-2 text-white bg-gradient-to-r from-indigo-600 to-indigo-700">
                  <RotateCcw className="w-4 h-4" /> Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* MOBILE FILTER BUTTON */}
          <button onClick={() => setMobileFilterOpen(true)}
            className="lg:hidden fixed bottom-6 right-6 z-50 text-white px-5 sm:px-6 py-3 sm:py-4 rounded-full shadow-2xl flex items-center gap-2 sm:gap-3 font-bold hover:scale-105 active:scale-95 transition-all bg-gradient-to-r from-indigo-600 to-indigo-700">
            <SlidersHorizontal className="w-5 h-5" /> 
            <span className="text-sm sm:text-base">Filters</span>
            {Object.keys(apiFilters).length > 0 && (
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-indigo-600" />
              </span>
            )}
          </button>
        </main>
      </div>

      {/* CHECKOUT MODAL */}
      {showCheckout && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[70] p-4">
          <div className={`${theme.popupBg} rounded-[2rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-indigo-500/10`}>
            <div className={`sticky top-0 p-5 sm:p-6 border-b z-10 flex items-center justify-between backdrop-blur-md ${isDarkMode ? 'bg-slate-800/90 border-slate-700' : 'bg-white/90 border-slate-100'}`}>
              <div>
                <h2 className={`text-xl sm:text-2xl font-bold ${theme.textMain}`}>Confirm Selection</h2>
                <p className={`text-sm ${theme.textMuted}`}>You are one step away from your future.</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                <X className="w-6 h-6 text-slate-500" />
              </button>
            </div>
            <div className="p-5 sm:p-8">
              <div className={`p-4 sm:p-5 rounded-2xl mb-6 sm:mb-8 flex gap-4 items-start border ${isDoubleFee ? 'bg-amber-100 border-amber-200' : 'bg-emerald-100 border-emerald-200'}`}>
                <div className={`p-2 rounded-full ${isDoubleFee ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                  {isDoubleFee ? <Zap className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                </div>
                <div>
                  <h4 className={`font-bold text-base sm:text-lg ${isDoubleFee ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {isDoubleFee ? 'Double Fee Tier Active' : 'Standard Fee Tier Active'}
                  </h4>
                  <p className={`text-xs sm:text-sm mt-1 font-medium ${isDoubleFee ? 'text-amber-600/80' : 'text-emerald-600/80'}`}>
                    {isDoubleFee ? `You selected ${selectionCount} colleges (> 5).` : `You selected ${selectionCount} colleges (≤ 5). Great value!`}
                  </p>
                </div>
              </div>
              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto custom-scrollbar pr-2">
                {selectedColleges.map((c, index) => (
                  <div key={getCollegeId(c)} className={`flex justify-between items-center p-3 sm:p-4 rounded-xl border ${theme.card}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-bold text-slate-400 w-5">{(index + 1).toString().padStart(2, '0')}</span>
                      <span className={`font-bold text-sm truncate ${theme.textMain}`}>{c.collegeName}</span>
                    </div>
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-2" />
                  </div>
                ))}
              </div>
              <button onClick={handleProceedToForm} disabled={proceedingToForm}
                className="w-full py-4 rounded-xl text-white font-bold text-base sm:text-lg shadow-xl shadow-indigo-500/30 flex items-center justify-center gap-3 hover:-translate-y-1 transition-all bg-gradient-to-r from-indigo-600 to-indigo-700 disabled:opacity-60">
                {proceedingToForm ? <Loader2 className="animate-spin" /> : <>Proceed to Application <IconArrowRight /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(99, 102, 241, 0.2); border-radius: 20px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: rgba(99, 102, 241, 0.5); }
        .bg-dots-pattern {
          background-image: radial-gradient(${isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'} 1px, transparent 1px);
          background-size: 20px 20px;
        }
        @media (max-width: 375px) {
          .xs\\:grid-cols-2 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
        }
      `}</style>
    </div>
  );
}